import React from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faMusic,
  faUsers,
  faTrash,
  faUpload,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';

/**
 * Event options portal component with conditional rendering for party events
 * and responsive dark/light theme support
 * 
 * @param {boolean} show - Whether to show the portal
 * @param {object} position - Position of the portal {x, y}
 * @param {function} onClose - Function to close the portal
 * @param {function} onEditEvent - Function to edit event
 * @param {function} onEditSetlist - Function to edit setlist
 * @param {function} onEditAttendees - Function to edit attendees
 * @param {function} onDeleteEvent - Function to delete event
 * @param {function} onUploadImage - Function to upload image
 * @param {boolean} canEdit - Whether user can edit the event
 * @param {boolean} isPartyEvent - Whether the event is a party type
 * @returns {JSX.Element} Portal component
 */
const EventOptionsPortal = ({
  show,
  position,
  onClose,
  onEditEvent,
  onEditSetlist,
  onEditAttendees,
  onDeleteEvent,
  onUploadImage,
  canEdit = false,
  isPartyEvent = false
}) => {
  const { t } = useLanguage();
  if (!show) return null;

  // Check if we're on mobile (you can adjust this breakpoint as needed)
  const isMobile = window.innerWidth <= 991.98;

  // Style for the portal menu - responsive dark/light theme
  const style = {
    position: 'fixed',
    top: position.y + 'px',
    right: position.x + 'px',
    zIndex: 1050,
    backgroundColor: isMobile ? '#333333' : '#ffffff',
    color: isMobile ? '#ffffff' : '#212529',
    padding: '0.5rem 0',
    borderRadius: '0.5rem',
    boxShadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.15)',
    transform: 'translateX(95%)',
    maxWidth: '200px',
    minWidth: '160px',
    border: isMobile ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
  };

  // Style for menu items - responsive
  const menuItemStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    color: isMobile ? '#ffffff' : '#333333',
    textDecoration: 'none',
    transition: 'background-color 0.2s ease',
    whiteSpace: 'nowrap',
    fontSize: '0.85rem',
    border: 'none',
    background: 'transparent',
    width: '100%',
    textAlign: 'left',
  };

  // Style for hover effect - responsive
  const hoverEffect = {
    backgroundColor: isMobile ? '#444444' : '#f8f9fa',
  };

  // Style for divider - responsive
  const dividerStyle = {
    margin: '0.5rem 0',
    borderTop: `1px solid ${isMobile ? 'rgba(255, 255, 255, 0.1)' : '#e9ecef'}`,
  };

  // Style for destructive action - responsive
  const destructiveStyle = {
    ...menuItemStyle,
    color: isMobile ? '#ff6b6b' : '#dc3545',
  };

  // Style for close button positioned at the top right - responsive
  const closeButtonStyle = {
    position: 'absolute',
    top: '0.25rem',
    right: '0.25rem',
    cursor: 'pointer',
    zIndex: 1060,
    backgroundColor: 'transparent',
    border: 'none',
    padding: '0.125rem 0.25rem',
    fontSize: '0.75rem',
    color: isMobile ? '#b3b3b3' : '#6c757d',
  };

  // Handle mouse enter/leave for hover effects
  const handleMouseEnter = (e, isDestructive = false) => {
    if (!isMobile) { // Only apply hover effects on desktop
      Object.assign(e.currentTarget.style, hoverEffect);
    }
  };

  const handleMouseLeave = (e, isDestructive = false) => {
    if (!isMobile) { // Only apply hover effects on desktop
      Object.assign(e.currentTarget.style, isDestructive ? destructiveStyle : menuItemStyle);
    }
  };

  // Portal body content
  const portalContent = (
    <div style={style} id="event-options-portal" aria-label="Event options menu">
      <button
        style={closeButtonStyle}
        onClick={onClose}
        aria-label="Close menu"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
      
      {/* Edit options - only show if user can edit */}
      {canEdit && (
        <>
          {/* Edit event details */}
          <button
            style={menuItemStyle}
            onClick={onEditEvent}
            onMouseEnter={(e) => handleMouseEnter(e)}
            onMouseLeave={(e) => handleMouseLeave(e)}
          >
            <FontAwesomeIcon icon={faEdit} className="me-2" fixedWidth />
            <span>{t('events.actions.editDetails')}</span>
          </button>

          {/* Edit setlist - don't show for party events */}
          {onEditSetlist && !isPartyEvent && (
            <button
              style={menuItemStyle}
              onClick={onEditSetlist}
              onMouseEnter={(e) => handleMouseEnter(e)}
              onMouseLeave={(e) => handleMouseLeave(e)}
            >
              <FontAwesomeIcon icon={faMusic} className="me-2" fixedWidth />
              <span>{t('events.actions.editSetlist')}</span>
            </button>
          )}

          {/* Edit attendees */}
          <button
            style={menuItemStyle}
            onClick={onEditAttendees}
            onMouseEnter={(e) => handleMouseEnter(e)}
            onMouseLeave={(e) => handleMouseLeave(e)}
          >
            <FontAwesomeIcon icon={faUsers} className="me-2" fixedWidth />
            <span>{t('events.actions.editAttendees')}</span>
          </button>

          {/* Upload image */}
          {onUploadImage && (
            <button
              style={menuItemStyle}
              onClick={onUploadImage}
              onMouseEnter={(e) => handleMouseEnter(e)}
              onMouseLeave={(e) => handleMouseLeave(e)}
            >
              <FontAwesomeIcon icon={faUpload} className="me-2" fixedWidth />
              <span>{t('events.actions.uploadImage')}</span>
            </button>
          )}

          <div style={dividerStyle}></div>

          {/* Delete event - destructive action */}
          <button
            style={destructiveStyle}
            onClick={onDeleteEvent}
            onMouseEnter={(e) => handleMouseEnter(e, true)}
            onMouseLeave={(e) => handleMouseLeave(e, true)}
          >
            <FontAwesomeIcon icon={faTrash} className="me-2" fixedWidth />
            <span>{t('events.actions.deleteEvent')}</span>
          </button>
        </>
      )}
    </div>
  );

  // Create portal in the DOM
  return createPortal(
    portalContent,
    document.body
  );
};

export default EventOptionsPortal;